import * as THREE from 'three';

import { isCustomObject3D } from '@/utils/three/boundingBox';
import { isSpecialObject } from '@/types/enum/three';

/* eslint-disable @typescript-eslint/no-explicit-any*/
export const setColor = (object: THREE.Object3D, color: string | number): void => {
  if (object && (object as any).material) {
    const material = (object as any).material;
    if (material.color) material.color = new THREE.Color(color);
    else if (material.uniforms) material.uniforms.color.value = new THREE.Color(color);
  }
  object?.children?.forEach((child) => {
    if (isCustomObject3D(child) && setColor) setColor(child, color);
  });
};

export const getColor = (object: THREE.Object3D): string => {
  if (object && (object as any).material) {
    const material = (object as any).material;
    let color = material.color;
    if (color && typeof color.getHexString === 'function') return `#${color.getHexString()}`;
    if (material.uniforms) {
      color = material.uniforms.color.value;
      if (color && typeof color.getHexString === 'function') return `#${color.getHexString()}`;
    }
  }
  for (const child of object.children) {
    const color = getColor(child);
    if (color.length > 0) return color;
  }
  return '';
};

export const setOpacity = (object: THREE.Object3D, alpha: number): void => {
  if (object && (object as any).material) {
    const material = (object as any).material;
    material.opacity = alpha / 100.0;
    material.transparent = alpha !== 100;
    material.needsUpdate = true;
  }
  if (object)
    object.children.forEach((child) => {
      if (!isSpecialObject(child.name)) setOpacity(child, alpha);
    });
};

const isWireframe = (object: THREE.Object3D): boolean => {
  if (object && (object as any).material) {
    const material = (object as any).material;
    return material.wireframe;
  }
  return false;
};

const hasWireframe = (object: THREE.Object3D): boolean => {
  if (object && (object as any).isMesh && object.children.length > 0) {
    for (const child of object.children) {
      if (isWireframe(child)) return true;
    }
  }
  return false;
};

export const setWireframe = (object: THREE.Object3D, showWireframe: boolean): void => {
  if (object) {
    if ((object as any).isMesh && showWireframe && !hasWireframe(object)) {
      const wireframeMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        wireframe: true,
        transparent: true,
      });
      const wireframe = new THREE.Mesh((object as any).geometry, wireframeMaterial);
      object.add(wireframe);
    }
    if ((object as any).isMesh && !showWireframe && hasWireframe(object)) {
      const childIndex = object.children.findIndex((child) => isWireframe(child));
      if (childIndex >= 0) object.children.splice(childIndex, 1);
    }

    object.children.forEach((child) => {
      if (!isWireframe(child)) setWireframe(child, showWireframe);
    });
  }
};

export const setTextureFile = (object: THREE.Object3D, file: string | null): void => {
  if (object && (object as any).material) {
    const material = (object as any).material as THREE.MeshBasicMaterial;
    if (file) material.map = new THREE.TextureLoader().load(file);
    else material.map = null;
  }
};

export const setBumpTextureFile = async (object: THREE.Object3D, file: string | null): Promise<void> => {
  if (object && (object as any).material) {
    const material = (object as any).material as THREE.MeshPhongMaterial;
    if (file) {
      material.shininess = 20;
      material.bumpScale = 20;
      material.bumpMap = await new THREE.TextureLoader().load(file);
      material.needsUpdate = true;
    } else {
      material.bumpMap = null;
    }
  }
};

export const setTexture = (object: THREE.Object3D, texture: THREE.Texture | null): void => {
  if (object && (object as any).material) {
    const material = (object as any).material as THREE.MeshBasicMaterial;
    material.map = texture;
  }
};

export const getTexture = (object: THREE.Object3D): THREE.Texture | null => {
  if (object && (object as any).material) {
    const material = (object as any).material as THREE.MeshBasicMaterial;
    if (material.map) return material.map;
    if ((material as any).bumpMap) return (material as any).bumpMap;
    if ((material as any).uniforms && (material as any).uniforms.bumpMap) {
      return (material as any).uniforms.bumpMap.value;
    }
  }
  return null;
};

export const getMaterial = (object: THREE.Object3D): THREE.Material | null => {
  if (object && (object as any).material) {
    return (object as any).material as THREE.Material;
  }
  return null;
};

export const scaleTexture = (object: THREE.Object3D, scaleX = 1, scaleY = 1, offsetX = 0, offsetY = 0, aspectRatio = 1): void => {
  if (!object) return;
  const texture = getTexture(object);
  if (texture) {
    const scaleFactorX = 1.0 / scaleX;
    let offsetFactorX = -(scaleFactorX - 1) / 2;
    const scaleFactorY = 1.0 / (scaleY * aspectRatio);
    let offsetFactorY = -(scaleFactorY - 1) / 2;
    offsetFactorX -= scaleFactorX * offsetX;
    offsetFactorY -= scaleFactorY * offsetY;
    texture.repeat.set(scaleFactorX, scaleFactorY);
    texture.offset.set((scaleFactorX - 1 + offsetFactorX) * -1, (scaleFactorY - 1 + offsetFactorY) * -1);
    const material = getMaterial(object);
    if (material && (material as any).uniforms) {
      if ((material as any).uniforms.repeat) {
        (material as any).uniforms.repeat.x = texture.repeat.x;
        (material as any).uniforms.repeat.y = texture.repeat.y;
      }
      if ((material as any).uniforms.offset) {
        (material as any).uniforms.offset.x = texture.offset.x;
        (material as any).uniforms.offset.y = texture.offset.y;
      }
      material.needsUpdate = true;
    }
  }
};

export const setProjectionBumpMapping = (object: THREE.Object3D, file: string | null, color: string, bumpColor: string): void => {
  if (!file) {
    // eslint-disable-next-line no-prototype-builtins
    if (object.hasOwnProperty('material')) {
      const material = (object as any).material;
      if (material.type === 'MeshPhongMaterial') return;
    }
  }
  const vertexShader = `
    attribute float ignore;
    attribute float direction;
    attribute vec2 uv2;
    varying vec2 vUv;
    varying vec2 vUv2;
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying float vIgnore;
    varying float vDirection;

    void main() {
        vUv = uv;
        vUv2 = uv2;
        vIgnore = ignore;
        vDirection = direction;
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`;
  const fragmentShader = `
    precision highp float;
    uniform sampler2D bumpMap;
    uniform int isBumpMapSet;
    uniform float bumpScale;
    uniform float ignoreThreshold;
    uniform vec3 bumpColor;
    uniform vec3 color;
    uniform vec2 repeat;
    uniform vec2 offset;
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec2 vUv;
    varying vec2 vUv2;
    varying float vIgnore;
    varying float vDirection;

    vec3 calculateNormal(vec2 uv, float bumpScale) {
      vec2 texelSize = vec2(1.0 / 512.0);
      float left = texture2D(bumpMap, uv + vec2(-texelSize.x, 0.0)).r;
      float right = texture2D(bumpMap, uv + vec2(texelSize.x, 0.0)).r;
      float top = texture2D(bumpMap, uv + vec2(0.0, texelSize.y)).r;
      float bottom = texture2D(bumpMap, uv + vec2(0.0, -texelSize.y)).r;
      return normalize(vec3((right - left) * bumpScale, (bottom - top) * bumpScale, 1.0));
    }

    void main() {
      vec2 uv = vUv * repeat + offset;
      vec2 uv2 = vUv2 * repeat + offset;
      
      // Hole die Texturfarbe
      vec4 texColor = texture2D(bumpMap, uv);
      float height = (texColor.r + texColor.g + texColor.b) / 3.0;
      if (vIgnore > ignoreThreshold) height = 1.0;
      if (isBumpMapSet == 0) height = 1.0;
      
      // Berechne die gestörte Normale
      vec3 bumpNormal = vec3(0.0, 0.0, 1.0);
      if (isBumpMapSet == 1) {
        if (vIgnore >= 0.0 && vIgnore <= ignoreThreshold) bumpNormal = calculateNormal(uv, bumpScale);
        else if (vIgnore < 0.0) {
          bumpNormal = vec3(0.0, 0.0, 1.0);
          height = 1.0;
          //if (vDirection > 0.0) {
            //bumpNormal = calculateNormal(uv2, bumpScale);
            //texColor = texture2D(bumpMap, uv2);
            //height = (texColor.r + texColor.g + texColor.b) / 3.0;
          //} else bumpNormal = calculateNormal(uv, bumpScale);
        }
      }
      vec3 normal = normalize(vNormal + bumpNormal);
      
      // Lichtberechnung
      vec3 lightDir = normalize(vec3(1.0, 1.0, 1.0));
      float diffuse = max(dot(normal, lightDir), 0.0);
      
      vec3 viewDir = normalize(vPosition);
      vec3 reflectDir = reflect(-lightDir, normal);
      float specular = pow(max(dot(viewDir, reflectDir), 0.0), 64.0);
      
      vec3 finalColor = mix(bumpColor, color, height * 0.8);
      //vec3 finalColor = color;
      //if (height < 0.2) finalColor = bumpColor;
      finalColor = finalColor * (diffuse * 0.8 + 0.2) + bumpColor * specular;
      
      gl_FragColor = vec4(finalColor, 1.0);
    }`;

  new THREE.TextureLoader().load(file!, (texture) => {
    // apply material once texture is ready
    const material = new THREE.ShaderMaterial({
      uniforms: {
        bumpMap: { value: texture },
        isBumpMapSet: { value: texture ? 1 : 0 },
        bumpScale: { value: 20.0 },
        repeat: { value: texture ? texture.repeat : new THREE.Vector2(1, 1) }, // Repeat the texture 4 times in both directions
        offset: { value: texture ? texture.offset : new THREE.Vector2(0, 0) }, // Offset the texture by 0.5 in both directions
        color: { value: new THREE.Color(color) },
        bumpColor: { value: new THREE.Color(bumpColor) },
        ignoreThreshold: { value: 0.5 },
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
    });

    // eslint-disable-next-line no-prototype-builtins
    if (object.hasOwnProperty('material')) {
      (object as any).material = material;
    }
  });
};
